import { useNavigate } from "react-router-dom";
import { ArrowLeft, BarChart3, TrendingUp, PoundSterling, Truck,  Calendar } from "lucide-react";

function ReportsMenu() {
  const navigate = useNavigate();

  const reportOptions = [
    {
      id: "salesreport",
      label: "Sales Report",
      icon: BarChart3,
      description: "View daily, weekly, and monthly sales reports",
      color: "from-purple-500 to-pink-500",
      bgColor: "from-purple-50 to-pink-50",
      borderColor: "border-purple-200",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600"
    },
    {
      id: "salesgrowth",
      label: "Sales Growth Analysis",
      icon: TrendingUp,
      description: "Analyze sales growth trends over time",
      color: "from-blue-500 to-cyan-500",
      bgColor: "from-blue-50 to-cyan-50",
      borderColor: "border-blue-200",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600"
    },
    {
      id: "paidout",
      label: "Paidout Report",
      icon: PoundSterling,
      description: "Track payouts and commissions",
      color: "from-orange-500 to-red-500",
      bgColor: "from-orange-50 to-red-50",
      borderColor: "border-orange-200",
      iconBg: "bg-orange-100",
      iconColor: "text-orange-600"
    },
    {
      id: "driver",
      label: "Driver Report",
      icon: Truck,
      description: "View driver delivery locations and orders",
      color: "from-cyan-500 to-blue-500",
      bgColor: "from-cyan-50 to-blue-50",
      borderColor: "border-cyan-200",
      iconBg: "bg-cyan-100",
      iconColor: "text-cyan-600"
    },
    {
      id: "dailysales",
      label: "Daily Sales",
      icon: Calendar, // You'll need to import this
      description: "View detailed orders for a specific date",
      color: "from-green-500 to-emerald-500",
      bgColor: "from-green-50 to-emerald-50",
      borderColor: "border-green-200",
      iconBg: "bg-green-100",
      iconColor: "text-green-600"
    }
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 md:mb-12">
        <button
          onClick={() => navigate("/admin")}
          className="group flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-all mb-6 font-medium text-sm md:text-base"
          style={{ fontFamily: "Bambino" }}
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Dashboard</span>
        </button>

        <div className="flex items-center justify-center gap-3 mb-4">
          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 via-pink-500 to-blue-500 bg-clip-text text-transparent"
            style={{ fontFamily: "Bambino", lineHeight: "1.2" }}
          >
            Reports
          </h1>
          <BarChart3 className="w-8 h-8 md:w-12 md:h-12 text-purple-500" />
        </div>

        <p
          className="text-center text-gray-600 text-sm md:text-base"
          style={{ fontFamily: "Bambino" }}
        >
          Access all your business reports and analytics
        </p>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 md:gap-8">
        {reportOptions.map((option) => {
          const Icon = option.icon;
          return (
            <button
              key={option.id}
              onClick={() => {
                if (option.id === "salesreport") navigate("/admin/reports/salesreport");
                else if (option.id === "salesgrowth") navigate("/admin/reports/salesgrowth");
                else if (option.id === "paidout") navigate("/admin/reports/paidout");
                else if (option.id === "driver") navigate("/admin/reports/driver");
                else if (option.id === "dailysales") navigate("/admin/reports/dailysales");
              }}
              className={`group relative bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 border-2 ${option.borderColor} overflow-hidden`}
            >
              {/* Gradient Background on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${option.bgColor} opacity-0 group-hover:opacity-50 transition-opacity duration-300 rounded-2xl md:rounded-3xl`}></div>

              <div className="relative flex flex-col items-center text-center space-y-4">
                {/* Icon Container */}
                <div className={`w-16 h-16 md:w-20 md:h-20 ${option.iconBg} rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                  <Icon className={`w-8 h-8 md:w-10 md:h-10 ${option.iconColor}`} />
                </div>

                {/* Title */}
                <h2
                  className="text-lg md:text-2xl font-bold text-gray-800"
                  style={{ fontFamily: "Bambino" }}
                >
                  {option.label}
                </h2>

                {/* Description */}
                <p
                  className="text-gray-600 text-xs md:text-sm"
                  style={{ fontFamily: "Bambino" }}
                >
                  {option.description}
                </p>

                {/* Action Button */}
                <div className="mt-4 pt-4 border-t border-gray-200 w-full">
                  <div className={`inline-block bg-gradient-to-r ${option.color} text-white px-4 md:px-6 py-2 md:py-2.5 rounded-lg md:rounded-xl text-xs md:text-sm font-bold group-hover:shadow-lg transition-all`}>
                    View Report
                  </div>
                </div>
              </div>

              {/* Decorative corner accent */}
              <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${option.color} opacity-0 group-hover:opacity-5 rounded-bl-3xl transition-opacity duration-300`}></div>
            </button>
          );
        })}
      </div>

      {/* Info Box */}
      <div className="mt-12 bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-6 md:p-8 border-2 border-purple-100 shadow-lg">
        <h3
          className="text-lg md:text-xl font-bold text-gray-800 mb-3 flex items-center gap-2"
          style={{ fontFamily: "Bambino" }}
        >
          <span className="inline-block w-2 h-2 bg-gradient-to-r from-purple-600 to-pink-500 rounded-full"></span>
          Quick Tips
        </h3>
        <ul className="space-y-2 text-sm md:text-base text-gray-700" style={{ fontFamily: "Bambino" }}>
          <li>• <strong>Sales Report</strong> provides detailed breakdowns by date, week, or month</li>
          <li>• <strong>Sales Growth Analysis</strong> shows trends and comparisons over custom periods</li>
          <li>• <strong>Paidout Report</strong> tracks all payouts and commissions</li>
          <li>• <strong>Driver Report</strong> displays delivery locations and order summaries by driver</li>
          <li>• <strong>Daily Sales</strong> allows you to view all orders for any specific date</li>
        </ul>
      </div>
    </div>
  );
}

export default ReportsMenu;